import moment from 'moment';
import { BrandType, HamburgerMenuType } from 'utils/types';

export type AuthInfo = {
  token: string;
  exchange: string;
  expiresIn: number;
};

class AccessControlUtils {
  static CMS_CLIENT_ID = 'd-signals';
  static EXPIRATION_MINUTES = 5;
  static AUTH_INFO_STORAGE_KEY: string = 'd-signals:auth-info';

  static getBearerGrant() {
    return process.env.REACT_APP_BEARER_GRANT;
  }

  static getLoginUrl() {
    const authApiUrl = process.env.REACT_APP_AUTH_API_URL;
    const frontEndUrl = process.env.REACT_APP_URL;
    return `${authApiUrl}/login?client_id=${AccessControlUtils.CMS_CLIENT_ID}&redirect_uri=${frontEndUrl}/auth`;
  }

  static getBaseCmsUrl() {
    return `${process.env.REACT_APP_BASE_CMS_URL}`;
  }

  static getCmsLoginUrl() {
    return `${AccessControlUtils.getBaseCmsUrl()}/client/login`;
  }

  static getCmsLogoutUrl() {
    return `${AccessControlUtils.getBaseCmsUrl()}/client/logout`;
  }

  static getChangeBrandUrl() {
    return `${AccessControlUtils.getBaseCmsUrl()}/client/changeBrand`;
  }

  static getRefreshTokenUrl(exchangeToken) {
    const authApiUrl = process.env.REACT_APP_AUTH_API_URL;
    return `${authApiUrl}/getToken?grant_type=authorization_code&code=${exchangeToken}`;
  }

  static getUserInfoUrl() {
    const authApiUrl = process.env.REACT_APP_AUTH_API_URL;
    return `${authApiUrl}/me`;
  }

  static isTokenExpired(
    expirationDateTime: string,
    currentTime: moment.Moment = moment()
  ): boolean {
    const difference = moment(expirationDateTime).diff(currentTime, 'minutes');
    return difference < AccessControlUtils.EXPIRATION_MINUTES;
  }

  static mapHamburgerMenuItems(activeClient: any, baseUrl: string = ''): Array<HamburgerMenuType> {
    let result = [];
    try {
      const availableProjects = activeClient.projects.filter(project => project.is_visible === '1');
      result = availableProjects.map(project => {
        const result = {
          name: project.project_name,
          icon: project.icon_html,
          selected: project.project_id === '12', // Signals V2, hardcoded, talked with Alex Nikitin
          url: null
        };
        const leftMenu = project.project_navigation.find(
          projectNavigation => projectNavigation.title === 'left_menu'
        );
        if (leftMenu) {
          const child = leftMenu.childs.find(child => child.is_default);
          if (child && child.url !== undefined) {
            if (child.url.startsWith('/')) {
              result.url = `${baseUrl}${child.url}`;
            } else {
              result.url = child.url;
            }
          }
        }
        return result;
      });
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  static getInvalidateTokenUrl(exchange: string) {
    const authApiUrl = process.env.REACT_APP_AUTH_API_URL;
    return `${authApiUrl}/invalidateToken?exchange=${exchange}`;
  }

  static getEnterpriseClient(activeClient: any, clientCategory: any): BrandType {
    let result: BrandType = null;
    if (clientCategory === 'ENTERPRISE') {
      result = {
        id: activeClient.client_id,
        name: activeClient.client_name.toUpperCase()
      };
    }
    return result;
  }

  static getBrandsListByClient(brandsList: any): Array<BrandType> {
    let result: Array<BrandType> = [];
    if (brandsList) {
      brandsList.forEach(item => {
        result.push({ id: item.client, name: item.brand });
      });
    }
    return result;
  }

  static isWhiteListedForCampaign(clientId: String, whiteList: Array<String>): Boolean {
    return !!whiteList.find(client => client === clientId);
  }

  static getAuthInfoFromStorage(): AuthInfo {
    const info: string = localStorage.getItem(AccessControlUtils.AUTH_INFO_STORAGE_KEY);
    if (info) {
      return JSON.parse(info) as AuthInfo;
    }
    return null;
  }

  static setAuthInfoFromStorage(authInfo: AuthInfo): void {
    localStorage.setItem(AccessControlUtils.AUTH_INFO_STORAGE_KEY, JSON.stringify(authInfo));
  }

  static clearAuthInfoFromStorage() {
    localStorage.removeItem(AccessControlUtils.AUTH_INFO_STORAGE_KEY);
  }
}

export default AccessControlUtils;
